// FinKloz — Fixed Assets View (Property, Plant & Equipment / Immobilisations)
// Replaces the mock DepreciationView for the bs-nca-ppe panel.
//
// 6 sections via sub-tabs:
//   1. Register          — list of all active assets
//   2. Acquisitions      — review new entries on PP&E GL accounts (live Sage)
//   3. Expense audit     — flag expense entries that should be capitalized
//   4. Depreciation      — auto-computed schedule
//   5. Journal entries   — generated JE for acq / dep / disposal
//   6. Reconciliation    — register vs Sage GL balances

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { sans, mono, green, greenLight, red, amber, useTheme, useCurrency } from '../tokens';
import { fmtCompactC } from '../helpers';
import { supabase } from '../supabaseClient';

// PP&E GL accounts mapping per category (cost / accumulated amortization / depreciation expense)
const GL_BY_CATEGORY = {
  'LHI':        { cost: '15010', accum: '15015', depExp: '90015', name: 'Leasehold Improvements' },
  'Clean Room': { cost: '15020', accum: '15025', depExp: '90025', name: 'Cleanroom' },
  'M&E':        { cost: '15030', accum: '15035', depExp: '90035', name: 'Machinery & Equipment' },
  'F&F':        { cost: '15040', accum: '15045', depExp: '90045', name: 'Furniture & Fixture' },
  'CE':         { cost: '15050', accum: '15055', depExp: '90055', name: 'Computer Equipment' },
};
const PPE_GL_ACCOUNTS = Object.values(GL_BY_CATEGORY).map(m => m.cost);   // ['15010','15020','15030','15040','15050']
const PPE_ACCUM_ACCOUNTS = Object.values(GL_BY_CATEGORY).map(m => m.accum); // ['15015','15025','15035','15045','15055']

// Capitalization thresholds
const THRESHOLDS = {
  machine: 5000,    // Machinery, Cleanroom
  furniture: 2000,  // Furniture
  computer: 2000,   // Computer equipment
  lhi: 0,           // No threshold for LHI
};

// Expense GL accounts to scan for missed capitalizations (Section 3)
const EXPENSE_GL_TO_SCAN = [
  { code: '51011', name: 'Device Component', tier: 'machine' },
  { code: '51040', name: 'Tools & Machine Tooling', tier: 'machine' },
  { code: '51060', name: 'Small Machines', tier: 'machine' },
  { code: '51050', name: 'Machine Repairs & Maintenance', tier: 'machine' },
  { code: '66030', name: 'IT Supplies & Small Equipments', tier: 'computer' },
  { code: '66060', name: 'Office & Team Supplies', tier: 'furniture' },
  { code: '65040', name: 'Office Cleaning & Maintenance', tier: 'machine' },
  { code: '65050', name: 'Repairs & Maintenance', tier: 'machine' },
];

const CATEGORY_LABEL = {
  'LHI': 'Leasehold Improvements',
  'M&E': 'Machinery & Equipment',
  'Clean Room': 'Cleanroom',
  'F&F': 'Furniture & Fixture',
  'CE': 'Computer Equipment',
};

const fmtMoney = (n) => {
  if (n == null || isNaN(n)) return '—';
  const sign = n < 0 ? '-' : '';
  return sign + '$' + Math.abs(n).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const fmtDate = (s) => {
  if (!s) return '—';
  return s.length >= 10 ? s.slice(0, 10) : s;
};

// ───────────────────────────────────────────────────────────────────────────────
// SECTION 1 — REGISTER
// ───────────────────────────────────────────────────────────────────────────────
function RegisterSection({ assets, t, loading, onDispose, onToggleDepreciate }) {
  const [filterCat, setFilterCat] = useState('all');
  const [filterStatus, setFilterStatus] = useState('active');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState({ field: 'date_in_service', dir: 'desc' });

  const filtered = useMemo(() => {
    let f = assets;
    if (filterStatus !== 'all') f = f.filter(a => a.status === filterStatus);
    if (filterCat !== 'all') f = f.filter(a => a.category_code === filterCat);
    if (search) {
      const q = search.toLowerCase();
      f = f.filter(a => (a.vendor_description || '').toLowerCase().includes(q)
                      || (a.reference || '').toLowerCase().includes(q)
                      || (a.gl_account || '').includes(q));
    }
    return [...f].sort((a, b) => {
      const va = a[sortBy.field], vb = b[sortBy.field];
      let cmp = 0;
      if (typeof va === 'number') cmp = (va || 0) - (vb || 0);
      else cmp = String(va || '').localeCompare(String(vb || ''));
      return sortBy.dir === 'asc' ? cmp : -cmp;
    });
  }, [assets, filterCat, filterStatus, search, sortBy]);

  const totals = useMemo(() => {
    return filtered.reduce((acc, a) => ({
      count: acc.count + 1,
      cost: acc.cost + (Number(a.cost) || 0),
      accum: acc.accum + (Number(a.accumulated_depreciation_baseline) || 0),
      monthly: acc.monthly + (Number(a.monthly_depreciation) || 0),
    }), { count: 0, cost: 0, accum: 0, monthly: 0 });
  }, [filtered]);

  const setSort = (field) => {
    setSortBy(prev => prev.field === field
      ? { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { field, dir: 'asc' });
  };

  const sortIcon = (field) => sortBy.field !== field ? '' : (sortBy.dir === 'asc' ? ' ↑' : ' ↓');

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: t.textMuted, fontSize: 13 }}>Loading fixed assets…</div>;

  return (
    <div>
      {/* Filters bar */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <select value={filterCat} onChange={e => setFilterCat(e.target.value)} style={inputStyle(t)}>
          <option value="all">All categories</option>
          {Object.entries(CATEGORY_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={inputStyle(t)}>
          <option value="active">Active only</option>
          <option value="disposed">Disposed only</option>
          <option value="all">All statuses</option>
        </select>
        <input
          placeholder="Search vendor, reference, GL…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ ...inputStyle(t), flex: 1, minWidth: 200 }}
        />
        <span style={{ fontSize: 11, color: t.textMuted, fontFamily: sans }}>
          {filtered.length} of {assets.length}
        </span>
      </div>

      {/* KPI summary */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 14 }}>
        <KpiCard t={t} label="Total Cost" value={fmtMoney(totals.cost)} />
        <KpiCard t={t} label="Accum. Depreciation" value={fmtMoney(totals.accum)} color={red} />
        <KpiCard t={t} label="Net Book Value" value={fmtMoney(totals.cost - totals.accum)} color={green} />
        <KpiCard t={t} label="Monthly Depreciation" value={fmtMoney(totals.monthly)} />
      </div>

      {/* Table */}
      <div style={{ background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, fontFamily: sans }}>
            <thead>
              <tr style={{ background: t.bgSurface, borderBottom: `2px solid ${t.border}` }}>
                <Th onClick={() => setSort('category_code')} t={t}>Cat{sortIcon('category_code')}</Th>
                <Th onClick={() => setSort('date_in_service')} t={t}>Date{sortIcon('date_in_service')}</Th>
                <Th t={t}>GL</Th>
                <Th onClick={() => setSort('vendor_description')} t={t}>Vendor / Description{sortIcon('vendor_description')}</Th>
                <Th onClick={() => setSort('cost')} t={t} align="right">Cost{sortIcon('cost')}</Th>
                <Th onClick={() => setSort('useful_life_years')} t={t} align="right">Life (yr)</Th>
                <Th onClick={() => setSort('monthly_depreciation')} t={t} align="right">Mo. Dep.{sortIcon('monthly_depreciation')}</Th>
                <Th t={t} align="right">Accum Dep.</Th>
                <Th t={t} align="right">NBV</Th>
                <Th t={t}>Status</Th>
                <Th t={t}>Depr.</Th>
                <Th t={t}></Th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((a) => {
                const nbv = (Number(a.cost) || 0) - (Number(a.accumulated_depreciation_baseline) || 0);
                return (
                  <tr key={a.id} style={{ borderBottom: `1px solid ${t.borderLight}` }}>
                    <Td t={t}>
                      <span style={{ display: 'inline-block', padding: '2px 6px', borderRadius: 3, background: catColor(a.category_code).bg, color: catColor(a.category_code).fg, fontSize: 9, fontWeight: 600 }}>
                        {a.category_code}
                      </span>
                    </Td>
                    <Td t={t} mono>{fmtDate(a.date_in_service)}</Td>
                    <Td t={t} mono>{a.gl_account}</Td>
                    <Td t={t}>{a.vendor_description}</Td>
                    <Td t={t} mono align="right">{fmtMoney(a.cost)}</Td>
                    <Td t={t} mono align="right">{Number(a.useful_life_years || 0).toFixed(2)}</Td>
                    <Td t={t} mono align="right">{fmtMoney(a.monthly_depreciation)}</Td>
                    <Td t={t} mono align="right" color={red}>{fmtMoney(a.accumulated_depreciation_baseline)}</Td>
                    <Td t={t} mono align="right" bold color={green}>{fmtMoney(nbv)}</Td>
                    <Td t={t}>
                      <span style={{ fontSize: 9, padding: '2px 6px', borderRadius: 3, background: a.status === 'active' ? greenLight : '#FEE2E2', color: a.status === 'active' ? green : red, fontWeight: 600 }}>
                        {a.status}
                      </span>
                    </Td>
                    <Td t={t}>
                      <button
                        onClick={() => onToggleDepreciate(a)}
                        title={a.depreciate === false ? 'Click to resume depreciation' : 'Click to pause depreciation'}
                        style={{
                          fontSize: 9, padding: '2px 8px', borderRadius: 3, fontWeight: 600,
                          border: 'none', cursor: 'pointer',
                          background: a.depreciate === false ? '#FEF3C7' : greenLight,
                          color: a.depreciate === false ? amber : green,
                        }}
                      >
                        {a.depreciate === false ? '⏸ Paused' : '▶ On'}
                      </button>
                    </Td>
                    <Td t={t}>
                      {a.status === 'active' && (
                        <button onClick={() => onDispose(a)} style={{ fontSize: 10, padding: '3px 8px', borderRadius: 4, border: `1px solid ${t.border}`, background: 'transparent', color: t.textSecondary, cursor: 'pointer' }}>
                          Dispose
                        </button>
                      )}
                    </Td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr style={{ background: t.bgSurface, borderTop: `2px solid ${t.border}`, fontWeight: 700 }}>
                <td colSpan={4} style={{ padding: '8px 10px', color: t.textPrimary }}>Totals ({totals.count} assets)</td>
                <td style={{ textAlign: 'right', padding: '8px 10px', fontFamily: mono, color: t.textPrimary }}>{fmtMoney(totals.cost)}</td>
                <td colSpan={1}></td>
                <td style={{ textAlign: 'right', padding: '8px 10px', fontFamily: mono, color: t.textPrimary }}>{fmtMoney(totals.monthly)}</td>
                <td style={{ textAlign: 'right', padding: '8px 10px', fontFamily: mono, color: red }}>{fmtMoney(totals.accum)}</td>
                <td style={{ textAlign: 'right', padding: '8px 10px', fontFamily: mono, color: green }}>{fmtMoney(totals.cost - totals.accum)}</td>
                <td colSpan={3}></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────────
// SECTION 2 — ACQUISITIONS À RÉVISER (live Sage)
// ───────────────────────────────────────────────────────────────────────────────
// Cost account → category_code reverse lookup
const COST_GL_TO_CATCODE = Object.entries(GL_BY_CATEGORY).reduce((acc, [code, m]) => {
  acc[m.cost] = code;
  return acc;
}, {});

// Default useful life (years) per category — matches PMD depreciation policy.
// Editable per-asset in the modal.
const DEFAULT_LIFE_YEARS = {
  'M&E': 15,         // Machinery & Equipment — 15 yrs
  'Clean Room': 10,  // Cleanroom — 10 yrs
  'F&F': 5,          // Furniture & Fixture — 5 yrs
  'CE': 3,           // Computer Equipment — 3 yrs
  'LHI': 10,         // Leasehold Improvements — over remaining lease term (~10 yrs)
};

function CapitalizeModal({ entry, t, onClose, onSaved }) {
  const net = (Number(entry.debit) || 0) - (Number(entry.credit) || 0);
  const catCode = COST_GL_TO_CATCODE[entry.account_no] || 'M&E';
  const [form, setForm] = useState({
    category_code: catCode,
    vendor_description: entry.description || '',
    date_in_service: entry.entry_date,
    cost: net,
    useful_life_years: DEFAULT_LIFE_YEARS[catCode] || 5,
    depreciate: true,
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const lifeMonths = (Number(form.useful_life_years) || 0) * 12;
  const monthlyDep = lifeMonths > 0 ? (Number(form.cost) || 0) / lifeMonths : 0;

  const save = async () => {
    setSaving(true);
    setErr('');
    try {
      const meta = GL_BY_CATEGORY[form.category_code] || {};
      const record = {
        company_id: '00000000-0000-0000-0000-000000000000',
        category: meta.name || CATEGORY_LABEL[form.category_code] || form.category_code,
        category_code: form.category_code,
        date_in_service: form.date_in_service,
        gl_account: meta.cost || entry.account_no,
        vendor_description: form.vendor_description,
        reference: entry.journal || null,
        cost: Number(form.cost) || 0,
        useful_life_years: Number(form.useful_life_years) || 0,
        monthly_depreciation: Math.round(monthlyDep * 100) / 100,
        accumulated_depreciation_baseline: 0,
        baseline_date: form.date_in_service,
        status: 'active',
        depreciate: form.depreciate,
        threshold_tier: form.category_code === 'LHI' ? 'lhi'
          : (form.category_code === 'CE' ? 'computer'
          : (form.category_code === 'F&F' ? 'furniture' : 'machine')),
        notes: `Capitalized from Sage entry ${entry.id} (${entry.account_no})`,
      };

      // Safety: never let the insert hang forever (15s timeout)
      const withTimeout = (p, ms) => Promise.race([
        p,
        new Promise((_, rej) => setTimeout(() => rej(new Error('Request timed out after ' + (ms / 1000) + 's — check connection / RLS')), ms)),
      ]);

      let { data, error } = await withTimeout(
        supabase.from('fixed_assets').insert([record]).select(),
        15000
      );

      // If the 'depreciate' column doesn't exist yet, retry without it
      if (error && /depreciate/i.test(error.message || '')) {
        const { depreciate, ...recordNoDep } = record;
        ({ data, error } = await withTimeout(
          supabase.from('fixed_assets').insert([recordNoDep]).select(),
          15000
        ));
      }

      if (error) {
        console.error('[Capitalize] insert error:', error);
        setErr(error.message || 'Insert failed (check RLS write policy on fixed_assets)');
        return;
      }

      // Log the decision — FIRE AND FORGET (never blocks the UI; was causing the hang)
      const newAssetId = data && data[0] ? data[0].id : null;
      supabase.from('fixed_asset_review_decisions').insert([{
        company_id: record.company_id,
        sage_gl_detail_id: entry.id,
        source_type: 'asset_account_review',
        account_no: entry.account_no,
        entry_date: entry.entry_date,
        vendor: entry.description,
        amount: net,
        decision: 'capitalized',
        decided_at: new Date().toISOString(),
        resulting_asset_id: newAssetId,
      }]).then(({ error: logErr }) => {
        if (logErr) console.warn('[Capitalize] decision log failed (non-blocking):', logErr.message);
      }).catch(e => console.warn('[Capitalize] decision log threw (non-blocking):', e));

      onSaved();
    } catch (e) {
      console.error('[Capitalize] threw:', e);
      setErr(String(e && e.message ? e.message : e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <div style={{ background: t.bgCard, borderRadius: 12, padding: 24, width: 480, maxWidth: '95vw', boxShadow: '0 8px 30px rgba(0,0,0,0.18)' }} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 15, fontWeight: 700, color: t.textPrimary, fontFamily: sans, marginBottom: 4 }}>Capitalize as Fixed Asset</div>
        <div style={{ fontSize: 11, color: t.textMuted, fontFamily: sans, marginBottom: 16 }}>
          Sage entry {fmtDate(entry.entry_date)} · {entry.account_no} · {fmtMoney(net)}
        </div>

        <Field label="Category" t={t}>
          <select value={form.category_code} onChange={e => {
            const c = e.target.value;
            setForm(f => ({ ...f, category_code: c, useful_life_years: DEFAULT_LIFE_YEARS[c] || 5 }));
          }} style={{ ...inputStyle(t), width: '100%' }}>
            {Object.entries(CATEGORY_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </Field>

        <Field label="Vendor / Description" t={t}>
          <input value={form.vendor_description} onChange={e => setForm(f => ({ ...f, vendor_description: e.target.value }))} style={{ ...inputStyle(t), width: '100%' }} />
        </Field>

        <div style={{ display: 'flex', gap: 10 }}>
          <Field label="Date in service" t={t} flex>
            <input type="date" value={form.date_in_service} onChange={e => setForm(f => ({ ...f, date_in_service: e.target.value }))} style={{ ...inputStyle(t), width: '100%' }} />
          </Field>
          <Field label="Cost (CAD)" t={t} flex>
            <input type="number" value={form.cost} onChange={e => setForm(f => ({ ...f, cost: e.target.value }))} style={{ ...inputStyle(t), width: '100%' }} />
          </Field>
        </div>

        <Field label="Useful life (years)" t={t}>
          <input type="number" step="0.5" value={form.useful_life_years} onChange={e => setForm(f => ({ ...f, useful_life_years: e.target.value }))} style={{ ...inputStyle(t), width: '100%' }} />
        </Field>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, cursor: 'pointer', fontSize: 12, color: t.textPrimary, fontFamily: sans }}>
          <input type="checkbox" checked={form.depreciate} onChange={e => setForm(f => ({ ...f, depreciate: e.target.checked }))} />
          Déprécier dès maintenant
          <span style={{ fontSize: 10, color: t.textMuted }}>(décoche pour mettre en pause — l'immo reste au registre sans amortissement)</span>
        </label>

        <div style={{ background: t.bgSurface, borderRadius: 8, padding: '10px 12px', margin: '8px 0 16px', fontSize: 12, fontFamily: sans, color: t.textSecondary }}>
          → Monthly depreciation (straight-line): <strong style={{ color: green, fontFamily: mono }}>{fmtMoney(monthlyDep)}</strong>
          <br />
          <span style={{ fontSize: 10, color: t.textMuted }}>
            JE: Dr {GL_BY_CATEGORY[form.category_code]?.cost} (asset) / Cr cash · then monthly Dr {GL_BY_CATEGORY[form.category_code]?.depExp} / Cr {GL_BY_CATEGORY[form.category_code]?.accum}
          </span>
        </div>

        {err && <div style={{ color: red, fontSize: 11, marginBottom: 10, fontFamily: sans }}>{err}</div>}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ ...btnStyle(t, t.textSecondary), padding: '7px 16px' }}>Cancel</button>
          <button onClick={save} disabled={saving} style={{ ...btnStyle(t, green), padding: '7px 16px', background: green, color: '#fff', opacity: saving ? 0.6 : 1 }}>
            {saving ? 'Saving…' : '✓ Capitalize & add to register'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, t, flex }) {
  return (
    <div style={{ marginBottom: 12, flex: flex ? 1 : undefined }}>
      <div style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: t.textMuted, fontFamily: sans, marginBottom: 4 }}>{label}</div>
      {children}
    </div>
  );
}

function AcquisitionsSection({ assets, t, month, onRefresh }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [capEntry, setCapEntry] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('sage_gl_detail')
          .select('id, month, entry_date, account_no, account_title, debit, credit, description, department, journal')
          .in('account_no', PPE_GL_ACCOUNTS)
          .eq('month', month)
          .order('entry_date', { ascending: false });
        if (error) console.error('[Acquisitions] sage fetch error:', error);
        setEntries(data || []);
      } catch (e) {
        console.error('[Acquisitions] threw:', e);
        setEntries([]);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [month]);

  return (
    <div>
      <div style={{ marginBottom: 14, fontSize: 11, color: t.textMuted, fontFamily: sans }}>
        Period: <strong style={{ color: t.textPrimary }}>{month}</strong> · PP&E cost accounts: {PPE_GL_ACCOUNTS.join(', ')}
      </div>

      {loading && <div style={{ padding: 40, textAlign: 'center', color: t.textMuted, fontSize: 13 }}>Loading Sage entries…</div>}
      {!loading && entries.length === 0 && (
        <div style={{ padding: 40, textAlign: 'center', color: t.textMuted, fontSize: 13, background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 10 }}>
          No new entries on PP&E accounts for {month}
        </div>
      )}
      {!loading && entries.length > 0 && (
        <div style={{ background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 10, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, fontFamily: sans, tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: 90 }} />
                <col style={{ width: 60 }} />
                <col />
                <col style={{ width: 55 }} />
                <col style={{ width: 90 }} />
                <col style={{ width: 90 }} />
                <col style={{ width: 100 }} />
                <col style={{ width: 90 }} />
                <col style={{ width: 130 }} />
              </colgroup>
              <thead>
                <tr style={{ background: t.bgSurface, borderBottom: `2px solid ${t.border}` }}>
                  <Th t={t}>Date</Th>
                  <Th t={t}>Account</Th>
                  <Th t={t}>Description</Th>
                  <Th t={t}>Dept</Th>
                  <Th t={t} align="right">Debit</Th>
                  <Th t={t} align="right">Credit</Th>
                  <Th t={t} align="right">Net</Th>
                  <Th t={t}>Status</Th>
                  <Th t={t}>Action</Th>
                </tr>
              </thead>
              <tbody>
                {entries.map(e => {
                  const net = (Number(e.debit) || 0) - (Number(e.credit) || 0);
                  const matched = assets.find(a =>
                    a.gl_account === e.account_no
                    && a.date_in_service === e.entry_date
                    && Math.abs((Number(a.cost) || 0) - net) < 1
                  );
                  return (
                    <tr key={e.id} style={{ borderBottom: `1px solid ${t.borderLight}` }}>
                      <td style={tdStyle(t, true, false)}>{fmtDate(e.entry_date)}</td>
                      <td style={tdStyle(t, true, false)}>{e.account_no}</td>
                      <td style={tdStyle(t, false, false)} title={e.description || ''}>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.description || '—'}</div>
                      </td>
                      <td style={tdStyle(t, true, false)}>{e.department || '—'}</td>
                      <td style={{ ...tdStyle(t, true, false), textAlign: 'right' }}>{fmtMoney(e.debit)}</td>
                      <td style={{ ...tdStyle(t, true, false), textAlign: 'right' }}>{fmtMoney(e.credit)}</td>
                      <td style={{ ...tdStyle(t, true, false), textAlign: 'right', fontWeight: 700 }}>{fmtMoney(net)}</td>
                      <td style={tdStyle(t, false, false)}>
                        {matched
                          ? <span style={{ fontSize: 9, padding: '2px 6px', borderRadius: 3, background: greenLight, color: green, fontWeight: 600 }}>✓ in register</span>
                          : <span style={{ fontSize: 9, padding: '2px 6px', borderRadius: 3, background: '#FEF3C7', color: amber, fontWeight: 600 }}>⚠ to review</span>
                        }
                      </td>
                      <td style={tdStyle(t, false, false)}>
                        {!matched && (
                          <button onClick={() => setCapEntry(e)} style={{ ...btnStyle(t, green), background: green, color: '#fff' }}>+ Capitaliser</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {capEntry && (
        <CapitalizeModal
          entry={capEntry}
          t={t}
          onClose={() => setCapEntry(null)}
          onSaved={() => { setCapEntry(null); if (onRefresh) onRefresh(); }}
        />
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────────
// SECTION 3 — EXPENSE AUDIT (find missed capitalizations)
// ───────────────────────────────────────────────────────────────────────────────
function ExpenseAuditSection({ t, month }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const codes = EXPENSE_GL_TO_SCAN.map(e => e.code);
        const [{ data, error }, dismissedRes] = await Promise.all([
          supabase
            .from('sage_gl_detail')
            .select('id, entry_date, account_no, account_title, debit, credit, description, department')
            .in('account_no', codes)
            .eq('month', month)
            .order('debit', { ascending: false }),
          supabase
            .from('fixed_asset_review_decisions')
            .select('sage_gl_detail_id')
            .eq('source_type', 'expense_audit')
            .eq('decision', 'expense_kept'),
        ]);
        if (error) console.error('[ExpenseAudit] sage fetch error:', error);

        // Set of sage entry ids already dismissed as "not capitalizable"
        const dismissed = new Set((dismissedRes && dismissedRes.data ? dismissedRes.data : []).map(d => d.sage_gl_detail_id));

        const flagged = (data || []).filter(e => {
          if (dismissed.has(e.id)) return false;
          const meta = EXPENSE_GL_TO_SCAN.find(x => x.code === e.account_no);
          if (!meta) return false;
          const amt = (Number(e.debit) || 0) - (Number(e.credit) || 0);
          return amt >= THRESHOLDS[meta.tier];
        });
        setEntries(flagged);
      } catch (e) {
        console.error('[ExpenseAudit] threw:', e);
        setEntries([]);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [month]);

  // Dismiss an expense entry (not capitalizable) — removes locally + logs decision
  const dismiss = (e) => {
    const amt = (Number(e.debit) || 0) - (Number(e.credit) || 0);
    setEntries(prev => prev.filter(x => x.id !== e.id));
    supabase.from('fixed_asset_review_decisions').insert([{
      company_id: '00000000-0000-0000-0000-000000000000',
      sage_gl_detail_id: e.id,
      source_type: 'expense_audit',
      account_no: e.account_no,
      entry_date: e.entry_date,
      vendor: e.description,
      amount: amt,
      decision: 'expense_kept',
      decided_at: new Date().toISOString(),
    }]).then(({ error: derr }) => {
      if (derr) console.warn('[ExpenseAudit] dismiss log failed (non-blocking):', derr.message);
    }).catch(err => console.warn('[ExpenseAudit] dismiss log threw:', err));
  };

  return (
    <div>
      <div style={{ marginBottom: 14, fontSize: 11, color: t.textMuted, fontFamily: sans }}>
        Period: <strong style={{ color: t.textPrimary }}>{month}</strong> · Thresholds — Machines ≥ ${THRESHOLDS.machine.toLocaleString()} · Furniture/Computers ≥ ${THRESHOLDS.furniture.toLocaleString()}
      </div>

      {loading && <div style={{ padding: 40, textAlign: 'center', color: t.textMuted, fontSize: 13 }}>Scanning expense accounts…</div>}
      {!loading && entries.length === 0 && (
        <div style={{ padding: 40, textAlign: 'center', color: green, fontSize: 13, background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 10 }}>
          ✓ No suspicious expense entries above thresholds for {month}
        </div>
      )}
      {!loading && entries.length > 0 && (
        <div style={{ background: t.bgCard, border: `1px solid ${amber}40`, borderRadius: 10, overflow: 'hidden' }}>
          <div style={{ padding: '10px 14px', background: '#FEF3C7', borderBottom: `1px solid ${amber}30`, fontSize: 12, fontWeight: 600, color: amber, fontFamily: sans }}>
            ⚠ {entries.length} entries above capitalization threshold — possible missed immobilisations
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, fontFamily: sans, tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: 90 }} />
                <col style={{ width: 220 }} />
                <col />
                <col style={{ width: 60 }} />
                <col style={{ width: 100 }} />
                <col style={{ width: 70 }} />
                <col style={{ width: 170 }} />
              </colgroup>
              <thead>
                <tr style={{ background: t.bgSurface, borderBottom: `2px solid ${t.border}` }}>
                  <Th t={t}>Date</Th>
                  <Th t={t}>Account</Th>
                  <Th t={t}>Description</Th>
                  <Th t={t}>Dept</Th>
                  <Th t={t} align="right">Amount</Th>
                  <Th t={t}>Tier</Th>
                  <Th t={t}>Action</Th>
                </tr>
              </thead>
              <tbody>
                {entries.map(e => {
                  const meta = EXPENSE_GL_TO_SCAN.find(x => x.code === e.account_no);
                  const amt = (Number(e.debit) || 0) - (Number(e.credit) || 0);
                  return (
                    <tr key={e.id} style={{ borderBottom: `1px solid ${t.borderLight}` }}>
                      <td style={tdStyle(t, true, false)}>{fmtDate(e.entry_date)}</td>
                      <td style={tdStyle(t, true, false)} title={`${e.account_no} ${meta?.name || ''}`}>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          <strong>{e.account_no}</strong> {meta?.name || ''}
                        </div>
                      </td>
                      <td style={tdStyle(t, false, false)} title={e.description || ''}>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.description || '—'}</div>
                      </td>
                      <td style={tdStyle(t, true, false)}>{e.department || '—'}</td>
                      <td style={{ ...tdStyle(t, true, false), textAlign: 'right', fontWeight: 700 }}>{fmtMoney(amt)}</td>
                      <td style={tdStyle(t, true, false)}>{meta?.tier}</td>
                      <td style={tdStyle(t, false, false)}>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                          <button style={btnStyle(t, green)} disabled title="Coming soon">→ Capitaliser</button>
                          <button
                            onClick={() => dismiss(e)}
                            title="Pas une immobilisation — retirer de la liste"
                            style={{
                              width: 22, height: 22, borderRadius: 4, lineHeight: 1,
                              border: `1px solid ${red}`, background: 'transparent',
                              color: red, cursor: 'pointer', fontWeight: 700, fontSize: 12,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}
                          >✕</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────────
// SECTION 4 — DEPRECIATION SCHEDULE
// ───────────────────────────────────────────────────────────────────────────────
function DepreciationSection({ assets, t }) {
  const [year, setYear] = useState(new Date().getFullYear());

  // Calendar-year months (Jan-Dec of selected year)
  const months = useMemo(() => {
    const arr = [];
    for (let i = 0; i < 12; i++) {
      const d = new Date(year, i, 1);
      arr.push({ key: `${year}-${String(i + 1).padStart(2, '0')}`, label: d.toLocaleString('en-CA', { month: 'short' }) });
    }
    return arr;
  }, [year]);

  // For each month in the year: include only assets that were in service that month and haven't ended their useful life
  const totalsByMonth = useMemo(() => {
    return months.map(m => {
      const [y, mo] = m.key.split('-').map(Number);
      const monthStart = new Date(y, mo - 1, 1);
      let total = 0;
      const byCat = {};
      assets.forEach(a => {
        if (a.status !== 'active') return;
        if (a.depreciate === false) return; // depreciation paused for this asset
        const dis = a.date_in_service ? new Date(a.date_in_service) : null;
        if (!dis || dis > monthStart) return; // not yet in service
        // End-of-life check: if cost has been fully depreciated, skip
        const lifeMonths = Math.round((Number(a.useful_life_years) || 0) * 12);
        if (lifeMonths > 0) {
          const monthsSinceService = (y - dis.getFullYear()) * 12 + (mo - 1 - dis.getMonth());
          if (monthsSinceService >= lifeMonths) return; // fully depreciated
        }
        const monthly = Number(a.monthly_depreciation) || 0;
        total += monthly;
        byCat[a.category_code] = (byCat[a.category_code] || 0) + monthly;
      });
      return { month: m, total, byCat };
    });
  }, [assets, months]);

  const totalAnnual = totalsByMonth.reduce((s, m) => s + m.total, 0);
  const yearOptions = [year - 1, year, year + 1, year + 2, year + 3];

  return (
    <div>
      <div style={{ background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 10, padding: '14px 18px', marginBottom: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: t.textPrimary, marginBottom: 4, fontFamily: sans }}>
            Depreciation Schedule — {year}
          </div>
          <div style={{ fontSize: 11, color: t.textMuted, fontFamily: sans }}>
            Method: straight-line · Annual total: <strong style={{ color: t.textPrimary }}>{fmtMoney(totalAnnual)}</strong>
          </div>
        </div>
        <select value={year} onChange={e => setYear(Number(e.target.value))} style={inputStyle(t)}>
          {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      <div style={{ background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, fontFamily: sans }}>
            <thead>
              <tr style={{ background: t.bgSurface, borderBottom: `2px solid ${t.border}` }}>
                <Th t={t}>Category</Th>
                {months.map(m => <Th key={m.key} t={t} align="right">{m.label}</Th>)}
                <Th t={t} align="right">Annual</Th>
              </tr>
            </thead>
            <tbody>
              {Object.keys(CATEGORY_LABEL).map(catCode => {
                const annual = totalsByMonth.reduce((s, m) => s + (m.byCat[catCode] || 0), 0);
                return (
                  <tr key={catCode} style={{ borderBottom: `1px solid ${t.borderLight}` }}>
                    <Td t={t}>
                      <span style={{ display: 'inline-block', padding: '2px 6px', borderRadius: 3, background: catColor(catCode).bg, color: catColor(catCode).fg, fontSize: 9, fontWeight: 600 }}>
                        {catCode}
                      </span>
                      <span style={{ marginLeft: 6, color: t.textSecondary }}>{CATEGORY_LABEL[catCode]}</span>
                    </Td>
                    {totalsByMonth.map(m => (
                      <Td key={m.month.key} t={t} mono align="right">{fmtMoney(m.byCat[catCode] || 0)}</Td>
                    ))}
                    <Td t={t} mono align="right" bold>{fmtMoney(annual)}</Td>
                  </tr>
                );
              })}
              <tr style={{ background: t.bgSurface, borderTop: `2px solid ${t.border}`, fontWeight: 700 }}>
                <Td t={t} bold>Total</Td>
                {totalsByMonth.map(m => <Td key={m.month.key} t={t} mono align="right" bold>{fmtMoney(m.total)}</Td>)}
                <Td t={t} mono align="right" bold>{fmtMoney(totalAnnual)}</Td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────────
// SECTION 5 — JOURNAL ENTRIES
// ───────────────────────────────────────────────────────────────────────────────
function JournalEntriesSection({ assets, t, month }) {
  const [posting, setPosting] = useState(false);
  const [postMsg, setPostMsg] = useState('');

  // Compute monthly depreciation JE preview using REAL category-specific GL accounts
  const previewJEs = useMemo(() => {
    const byCat = {};
    assets.filter(a => a.status === 'active' && a.depreciate !== false).forEach(a => {
      const monthly = Number(a.monthly_depreciation) || 0;
      const key = a.category_code;
      const map = GL_BY_CATEGORY[key] || {};
      if (!byCat[key]) byCat[key] = {
        glCost: map.cost || a.gl_account,
        glAccumDep: map.accum,
        glDepExp: map.depExp,
        total: 0,
        count: 0,
        label: CATEGORY_LABEL[key],
      };
      byCat[key].total += monthly;
      byCat[key].count += 1;
    });

    return Object.entries(byCat).map(([code, info]) => ({
      code,
      label: info.label,
      glCost: info.glCost,
      glAccumDep: info.glAccumDep,
      glDepExp: info.glDepExp,
      total: info.total,
      count: info.count,
    }));
  }, [assets]);

  // ── Export CSV (one Dr + one Cr line per category, balanced JE) ──
  const exportCSV = () => {
    const rows = [['Date', 'Journal', 'Account', 'Account Name', 'Debit', 'Credit', 'Memo']];
    const lastDay = (() => { const [y, m] = month.split('-').map(Number); return new Date(y, m, 0).toISOString().slice(0, 10); })();
    previewJEs.forEach(je => {
      const amt = (Math.round(je.total * 100) / 100).toFixed(2);
      const memo = `Monthly depreciation ${month} — ${je.label} (${je.count} assets)`;
      // Dr Depreciation Expense
      rows.push([lastDay, 'DEPR', je.glDepExp, `Depreciation - ${je.label}`, amt, '0.00', memo]);
      // Cr Accumulated Depreciation
      rows.push([lastDay, 'DEPR', je.glAccumDep, `Accum. Amort. - ${je.label}`, '0.00', amt, memo]);
    });
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `depreciation-JE-${month}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // ── Post JE to FinKloz ledger (audit trail in Supabase) ──
  // NOTE: this does NOT push to Sage Intacct. The Sage write API is not connected.
  // It records the generated entries so they're tracked; use the CSV to import into Sage.
  const postToLedger = async () => {
    if (!window.confirm(
      `Enregistrer les écritures de dépréciation de ${month} dans le journal FinKloz ?\n\n`
      + `⚠️ Ceci n'envoie RIEN vers Sage. Ça crée un enregistrement d'audit dans FinKloz. `
      + `Pour poster dans Sage, utilise "Export CSV" puis importe le fichier dans Sage Intacct.`
    )) return;
    setPosting(true);
    setPostMsg('');
    try {
      const lastDay = (() => { const [y, m] = month.split('-').map(Number); return new Date(y, m, 0).toISOString().slice(0, 10); })();
      const records = [];
      previewJEs.forEach(je => {
        const amt = Math.round(je.total * 100) / 100;
        records.push({
          fixed_asset_id: null,
          entry_type: 'depreciation',
          entry_date: lastDay,
          amount: amt,
          debit_account: je.glDepExp,
          credit_account: je.glAccumDep,
          description: `Monthly depreciation ${month} — ${je.label} (${je.count} assets)`,
          posted: false,
        });
      });
      const { error } = await supabase.from('fixed_asset_journal_entries').insert(records);
      if (error) {
        setPostMsg('Erreur: ' + error.message);
      } else {
        setPostMsg(`✓ ${records.length} écritures enregistrées dans le journal FinKloz (${month}).`);
      }
    } catch (e) {
      setPostMsg('Erreur: ' + (e && e.message ? e.message : String(e)));
    } finally {
      setPosting(false);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <span style={{ fontSize: 11, color: t.textMuted, fontFamily: sans }}>
          Period: <strong style={{ color: t.textPrimary }}>{month}</strong>
        </span>
        <div style={{ flex: 1 }} />
        {postMsg && <span style={{ fontSize: 11, color: postMsg.startsWith('✓') ? green : red, fontFamily: sans }}>{postMsg}</span>}
        <button onClick={exportCSV} style={{ ...btnStyle(t, green), background: green, color: '#fff' }}>📤 Export CSV</button>
        <button onClick={postToLedger} disabled={posting} style={{ ...btnStyle(t, t.textSecondary), opacity: posting ? 0.6 : 1 }} title="Enregistre dans le journal FinKloz (n'envoie pas vers Sage)">
          {posting ? 'Enregistrement…' : '📝 Post to FinKloz ledger'}
        </button>
      </div>

      <div style={{ background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 10, padding: '14px 18px', marginBottom: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: t.textPrimary, marginBottom: 8, fontFamily: sans }}>
          Monthly Depreciation JE preview ({month})
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, fontFamily: sans }}>
          <thead>
            <tr style={{ borderBottom: `2px solid ${t.border}` }}>
              <Th t={t}>Category</Th>
              <Th t={t} align="right">Assets</Th>
              <Th t={t}>Dr (Dep Expense)</Th>
              <Th t={t}>Cr (Accum Dep)</Th>
              <Th t={t} align="right">Amount</Th>
            </tr>
          </thead>
          <tbody>
            {previewJEs.map(je => (
              <tr key={je.code} style={{ borderBottom: `1px solid ${t.borderLight}` }}>
                <Td t={t}>{je.label}</Td>
                <Td t={t} mono align="right">{je.count}</Td>
                <Td t={t} mono>{je.glDepExp}</Td>
                <Td t={t} mono>{je.glAccumDep}</Td>
                <Td t={t} mono align="right" bold>{fmtMoney(je.total)}</Td>
              </tr>
            ))}
            <tr style={{ background: t.bgSurface, borderTop: `2px solid ${t.border}`, fontWeight: 700 }}>
              <Td t={t} bold>Total</Td>
              <Td t={t} mono align="right" bold>{previewJEs.reduce((s, j) => s + j.count, 0)}</Td>
              <Td t={t} colSpan={2}></Td>
              <Td t={t} mono align="right" bold>{fmtMoney(previewJEs.reduce((s, j) => s + j.total, 0))}</Td>
            </tr>
          </tbody>
        </table>
      </div>

      <div style={{ fontSize: 11, color: t.textMuted, fontStyle: 'italic', fontFamily: sans, padding: '0 4px' }}>
        💡 Comptes mappés depuis le Chart of Accounts Sage — Dr 90xxx (Dep Expense) / Cr 15xx5 (Accum. Amort.) par catégorie.
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────────
// SECTION 6 — RECONCILIATION (Cadrage)
// ───────────────────────────────────────────────────────────────────────────────
function ReconciliationSection({ assets, t, month }) {
  const [glBalances, setGlBalances] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('sage_gl_detail')
          .select('account_no, debit, credit, month')
          .in('account_no', PPE_GL_ACCOUNTS)
          .lte('month', month);
        if (error) console.error('[Reconciliation] sage fetch error:', error);
        const balances = {};
        (data || []).forEach(row => {
          const net = (Number(row.debit) || 0) - (Number(row.credit) || 0);
          balances[row.account_no] = (balances[row.account_no] || 0) + net;
        });
        setGlBalances(balances);
      } catch (e) {
        console.error('[Reconciliation] threw:', e);
        setGlBalances({});
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [month]);

  // Register at end of selected month: include assets in service ON OR BEFORE month-end,
  // and not disposed before month-end
  const monthEndDate = useMemo(() => {
    const [y, m] = month.split('-').map(Number);
    return new Date(y, m, 0).toISOString().slice(0, 10); // last day of month
  }, [month]);

  const registerByGL = useMemo(() => {
    const r = {};
    assets.forEach(a => {
      if (!a.date_in_service || a.date_in_service > monthEndDate) return;
      if (a.status === 'disposed' && a.disposal_date && a.disposal_date <= monthEndDate) return;
      r[a.gl_account] = (r[a.gl_account] || 0) + (Number(a.cost) || 0);
    });
    return r;
  }, [assets, monthEndDate]);

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: t.textMuted, fontSize: 13 }}>Loading GL balances…</div>;

  return (
    <div>
      <div style={{ marginBottom: 14, fontSize: 11, color: t.textMuted, fontFamily: sans }}>
        Period: <strong style={{ color: t.textPrimary }}>as of {monthEndDate}</strong> · cumulative Sage GL balance vs register state at month-end
      </div>
      <div style={{ background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 10, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, fontFamily: sans }}>
          <thead>
            <tr style={{ background: t.bgSurface, borderBottom: `2px solid ${t.border}` }}>
              <Th t={t}>GL Account</Th>
              <Th t={t}>Category</Th>
              <Th t={t} align="right">Sage GL Balance</Th>
              <Th t={t} align="right">Register Total Cost</Th>
              <Th t={t} align="right">Difference</Th>
              <Th t={t}>Status</Th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(GL_BY_CATEGORY).map(([catCode, map]) => {
              const sage = glBalances[map.cost] || 0;
              const register = registerByGL[map.cost] || 0;
              const diff = sage - register;
              const ok = Math.abs(diff) < 1;
              return (
                <tr key={map.cost} style={{ borderBottom: `1px solid ${t.borderLight}` }}>
                  <Td t={t} mono bold>{map.cost}</Td>
                  <Td t={t}>{map.name}</Td>
                  <Td t={t} mono align="right">{fmtMoney(sage)}</Td>
                  <Td t={t} mono align="right">{fmtMoney(register)}</Td>
                  <Td t={t} mono align="right" bold color={ok ? green : amber}>{fmtMoney(diff)}</Td>
                  <Td t={t}>
                    {ok
                      ? <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 3, background: greenLight, color: green, fontWeight: 600 }}>✓ Balanced</span>
                      : <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 3, background: '#FEF3C7', color: amber, fontWeight: 600 }}>⚠ {fmtMoney(Math.abs(diff))} diff</span>
                    }
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: 14, fontSize: 11, color: t.textMuted, fontFamily: sans, padding: '0 4px' }}>
        💡 Les soldes Sage = somme cumulée des debit-credit toutes périodes confondues. Le registre = coût d'acquisition × statut actif.
        Une différence indique soit des entrées Sage non encore importées dans le registre, soit des immos sorties non synchronisées.
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT — sub-tabs
// ───────────────────────────────────────────────────────────────────────────────
export default function FixedAssetsView({ panel, selectedMonth }) {
  // selectedMonth is the global month picker (e.g., '2026-04'). Fallback to current month.
  const month = selectedMonth || new Date().toISOString().slice(0, 7);
  const t = useTheme();
  const [section, setSection] = useState('register');
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAssets = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('fixed_assets')
        .select('*')
        .order('date_in_service', { ascending: false });
      if (error) {
        console.error('[FixedAssets] fetch error:', error.message, error);
        setAssets([]);
      } else {
        console.log('[FixedAssets] loaded', (data || []).length, 'rows');
        setAssets(data || []);
      }
    } catch (e) {
      console.error('[FixedAssets] fetch threw:', e);
      setAssets([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAssets();
    // Realtime
    const ch = supabase.channel('fixed-assets-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'fixed_assets' }, fetchAssets)
      .subscribe();
    return () => supabase.removeChannel(ch);
  }, [fetchAssets]);

  const handleDispose = async (asset) => {
    const proceeds = window.prompt(`Disposal proceeds for "${asset.vendor_description}" (CAD):`, '0');
    if (proceeds == null) return;
    const today = new Date().toISOString().slice(0, 10);
    await supabase.from('fixed_assets').update({
      status: 'disposed',
      disposal_date: today,
      disposal_proceeds: Number(proceeds) || 0,
      updated_at: new Date().toISOString(),
    }).eq('id', asset.id);
    fetchAssets();
  };

  const handleToggleDepreciate = async (asset) => {
    const next = asset.depreciate === false; // currently paused → resume
    const { error } = await supabase.from('fixed_assets').update({
      depreciate: next,
      updated_at: new Date().toISOString(),
    }).eq('id', asset.id);
    if (error) {
      window.alert('Could not toggle depreciation: ' + error.message + '\n\n(La colonne "depreciate" doit être ajoutée à la table — voir le SQL fourni.)');
      return;
    }
    fetchAssets();
  };

  const sections = [
    { key: 'register', label: 'Register', count: assets.filter(a => a.status === 'active').length },
    { key: 'acquisitions', label: 'Acquisitions to review' },
    { key: 'expense_audit', label: 'Expense audit' },
    { key: 'depreciation', label: 'Depreciation' },
    { key: 'journal', label: 'Journal entries' },
    { key: 'reconciliation', label: 'Reconciliation' },
  ];

  return (
    <div>
      {/* Subtitle (parent panel already shows the title + GL range) */}
      <div style={{ fontSize: 12, color: t.textMuted, fontFamily: sans, marginBottom: 14 }}>
        Immobilisations corporelles — {assets.length} total · {assets.filter(a => a.status === 'active').length} active
      </div>

      {/* Sub-tabs */}
      <div style={{ display: 'flex', gap: 4, borderBottom: `1px solid ${t.border}`, marginBottom: 16, overflowX: 'auto' }}>
        {sections.map(s => (
          <button
            key={s.key}
            onClick={() => setSection(s.key)}
            style={{
              padding: '8px 14px',
              border: 'none',
              borderBottom: section === s.key ? `2px solid ${green}` : '2px solid transparent',
              background: 'transparent',
              cursor: 'pointer',
              fontSize: 12,
              fontWeight: section === s.key ? 600 : 500,
              color: section === s.key ? green : t.textSecondary,
              fontFamily: sans,
              whiteSpace: 'nowrap',
              marginBottom: -1,
            }}
          >
            {s.label}{s.count != null && ` (${s.count})`}
          </button>
        ))}
      </div>

      {/* Section content */}
      {section === 'register' && <RegisterSection assets={assets} t={t} loading={loading} onDispose={handleDispose} onToggleDepreciate={handleToggleDepreciate} />}
      {section === 'acquisitions' && <AcquisitionsSection assets={assets} t={t} month={month} onRefresh={fetchAssets} />}
      {section === 'expense_audit' && <ExpenseAuditSection t={t} month={month} />}
      {section === 'depreciation' && <DepreciationSection assets={assets} t={t} />}
      {section === 'journal' && <JournalEntriesSection assets={assets} t={t} month={month} />}
      {section === 'reconciliation' && <ReconciliationSection assets={assets} t={t} month={month} />}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────────
// Helpers
// ───────────────────────────────────────────────────────────────────────────────
function Th({ children, onClick, t, align }) {
  return (
    <th onClick={onClick} style={{
      textAlign: align || 'left', padding: '8px 10px', fontSize: 10,
      color: t.textMuted, fontWeight: 600, textTransform: 'uppercase',
      letterSpacing: 0.5, cursor: onClick ? 'pointer' : 'default',
      userSelect: 'none', whiteSpace: 'nowrap',
    }}>{children}</th>
  );
}

function Td({ children, t, mono, align, bold, color, colSpan }) {
  return (
    <td colSpan={colSpan} style={{
      padding: '6px 10px',
      fontFamily: mono ? 'ui-monospace, Menlo, monospace' : 'inherit',
      textAlign: align || 'left',
      fontWeight: bold ? 700 : 400,
      color: color || t.textPrimary,
      fontSize: 11,
      whiteSpace: 'nowrap',
    }}>{children}</td>
  );
}

function KpiCard({ t, label, value, color }) {
  return (
    <div style={{ background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 10, padding: '12px 16px' }}>
      <div style={{ fontSize: 9, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: t.textMuted, fontFamily: sans, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, fontFamily: 'ui-monospace, Menlo, monospace', color: color || t.textPrimary }}>{value}</div>
    </div>
  );
}

function tdStyle(t, mono, nowrap) {
  return {
    padding: '6px 10px',
    fontFamily: mono ? 'ui-monospace, Menlo, monospace' : 'inherit',
    fontSize: 11,
    color: t.textPrimary,
    whiteSpace: nowrap ? 'nowrap' : 'normal',
    verticalAlign: 'top',
  };
}

function inputStyle(t) {
  return {
    fontSize: 11, fontFamily: 'inherit', padding: '6px 10px',
    border: `1px solid ${t.border}`, borderRadius: 6,
    background: t.inputBg, color: t.textPrimary, outline: 'none',
  };
}

function btnStyle(t, color) {
  return {
    fontSize: 10, fontWeight: 600, padding: '4px 10px',
    border: `1px solid ${color}`, background: 'transparent',
    color: color, borderRadius: 4, cursor: 'pointer',
    fontFamily: 'inherit',
  };
}

function catColor(code) {
  const map = {
    'LHI': { bg: '#DBEAFE', fg: '#1E40AF' },
    'M&E': { bg: '#FCE7F3', fg: '#BE185D' },
    'Clean Room': { bg: '#E0F2FE', fg: '#0369A1' },
    'F&F': { bg: '#FEF3C7', fg: '#92400E' },
    'CE': { bg: '#D1FAE5', fg: '#065F46' },
  };
  return map[code] || { bg: '#E5E7EB', fg: '#374151' };
}
